import ethereum from 'decentralized/ethereum.js';
import EthereumSoundsSearcher from 'search/EthereumSoundsSearcher.js';
import accounts from 'accounts/Accounts.js';
import MonthlyIndexGetter from '../MonthlyIndexGetter.js';
import {mergeIndices} from './mergeIndices.js';
import ipfs from 'decentralized/Ipfs.js';
import {NewIndex} from '../EthereumEvents.js';
import BackendQuery from '../BackendQuery.js';
import GuildsIndexContract from '../contracts/GuildsIndexContract.js';
import {TAG} from 'constants/field-types.js';
import {STYLE} from 'constants/field-types.js';
import {COUNTRY} from 'constants/field-types.js';
import {YEAR} from 'constants/field-types.js';

const indexContract = new GuildsIndexContract();

const DEFAULT_TAGS_SIZE = 300;
const monthlyIndexGetter = new MonthlyIndexGetter();

const indexCache = {};

class EthereumCache {
    constructor(store) {
        this.fullIndex = undefined;
        this.guildId = 0;

        this.useMonth = false;
        this.monthlyIndices = [];
        this.monthlyFilter = undefined;

        this.store = store;

        if (store) {
            this.store.subscribe(() => this.onStateUpdate());
        }
    }

    onStateUpdate() {
        let state = this.store.getState();
        let guildId = state.guilds.selectedGuild ? state.guilds.selectedGuild.guildId : 0;
        if (guildId !== this.guildId) {
            this.guildId = guildId;
            this.loadIndex();
        }
    }

    async loadNewIndex(guildId) {
        this.guildId = guildId;
        await this.loadIndex();
    }

    get index() {
        if (this.useMonth) {
            return this.monthlyIndex === undefined ?
                this.fullIndex : this.monthlyIndex.index;
        }

        return this.fullIndex;
    }

    get toBlock() {
        return this.monthlyIndex.toBlock;
    }

    get fromBlock() {
        return this.monthlyIndex.fromBlock;
    }

    async filterByMonth(month, year) {
        if (this.monthlyFilter === undefined ||
            (this.monthlyFilter.month !== month || this.monthlyFilter.year !== year)) {
            this.monthlyIndex =  await monthlyIndexGetter.getIndexForMonth(month, year);
        }
        this.useMonth = true;
        this.monthlyFilter = {
            month: month,
            year: year
        };

        return true;
    }

    clearFilter() {
        this.useMonth = false;
    }

    // loads the index from ethereum & ipfs
    async loadIndex(guildId=this.guildId) {
        if (guildId === 0 && accounts.isLoggedIn()) {
            // merge everything
            this.fullIndex = await this.fetchAndMergeIndices();
            return;
        }
        this.fullIndex = await this.fetchIndex(guildId);
    }

    async fetchAndMergeIndices() {
        let guilds = [0]; //await ethereum.guilds.getGuildsForUser();
        // guilds.push(0);
        let guildIds = Array.from(new Set(guilds.map(x => x.guildId)));
        let indices = [];
        for (let i in guildIds) {
            let guildId = guildIds[i];
            console.log('fetching index for guildId = ' + guildId);
            indices.push(await this.fetchIndex(guildId));
        }
        console.log('indices to merge = ');
        console.log(indices);

        return mergeIndices(indices);
    }

    async fetchIndex(guildId=this.guildId) {
        let hash = await this.getIndexHash(guildId);
        console.log('hash returned = ' + hash);
        if (hash === undefined) {
            return undefined;;
        }
        return await ipfs.getJson(hash);
    }

    // example: getAllForField(TAG) returns all tags (paginated)
    getAllForField(fieldType, start=0, size=1000) {
        if (this.index === undefined) {
            // we haven't loaded the index yet
            return [];
        }

        let fields = this.index[fieldType][fieldType]
            .map((x, i) => ({value: x.dimension, rank: i}));

        this.sort(fields);

        if (fields.length < start + size) {
            return fields.slice(start);
        }

        return fields.slice(start, start + size);
    }

    // example getFieldsForField(STYLES, TAGS, 'breaks')
    // returns all styles of music from sounds with TAGS=breaks
    getFieldsForField(typeA, typeB, field) {
        if (this.index === undefined) {
            return new Set([]);
        }

       let filtered = this.index[typeB][typeA].filter(
            obj => obj.dimension == field);

        if (filtered.length == 0){
            return new Set([]);
        }

        if (filtered[0].values === undefined) {
            return new Set();
        }
        let values = [...filtered[0].values].map(
            (val, i) => ({value: val, rank: i}));

        this.sort(values);

        return new Set(values);
    }

    freeTextSearch(field, search) {
        let fields = this.getAllForField(field, 0, 5000);
        fields = fields.filter(
            field => field.value.toString().toLowerCase().includes(search.toLowerCase()))
            .map((val, i) => ({value: val.value, rank: val.rank}));

        return fields;
    }

    sort(fields) {
        return fields.sort((a, b) => {
            a = a.value;
            b = b.value;
            if (a[0] === undefined) {
                return 1;
            } else if (b[0] === undefined) {
                return -1;
            }
            if (a[0] === a[0].toLocaleLowerCase() && b[0] === b[0].toLocaleLowerCase() ||
                a[0] === a[0].toLocaleUpperCase() && b[0] === b[0].toLocaleUpperCase()) {
                return a.localeCompare(b);
            }
            if (a[0] === a[0].toLocaleLowerCase()) {
                return -1;
            }
            return 1;
        });
    }

    async getIndexHash(guildId) {
        if (guildId in indexCache) {
            return await indexCache[guildId];
        }
        let promise = new Promise(async (resolve, reject) => {
            let query = new BackendQuery(indexContract.contractAddress)
                .from(NewIndex)
                .select("indexHash")
                // .fromBlock(await ethereum.web3.eth.getBlockNumber() - 50000)
                .where('guildId', guildId)
                .limit(1);
            
            let results = await query.executeQuery();
            
            if (results.length === 0) {
                resolve(undefined);
                return;
            }
            
            let index = results[0].indexHash;
            resolve(index);
        });
        indexCache[guildId] = promise;
        return await promise;
    }
}

export default EthereumCache;
